(() => {
  if (window.udDiscoveryLoaded) return;
  window.udDiscoveryLoaded = true;
  const readSession = key => { try { return sessionStorage.getItem(key); } catch { return null; } };
  const saveSession = (key, value) => { try { sessionStorage.setItem(key, value); } catch {} };
  class UDSmartMenu extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.hover = matchMedia('(hover: hover) and (pointer: fine)');
      if (this.dataset.mode === 'desktop') {
        window.addEventListener('wheel', e => {
          if (!this.panel || e.defaultPrevented || e.ctrlKey || e.deltaY <= 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
          const overPanel = this.panel.contains(e.target);
          const delta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);
          if (overPanel && e.cancelable) e.preventDefault();
          this.dismissForScroll();
          if (overPanel && e.defaultPrevented) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
        }, { signal, capture: true, passive: false });
        window.addEventListener('scroll', () => {
          if (this.panel && Math.abs(window.scrollY - this.openScrollY) > 4) this.dismissForScroll();
        }, { signal, passive: true });
        this.querySelectorAll('.ud-menu-item').forEach(item => {
          item.addEventListener('pointerenter', () => {
            if (!this.hover.matches) return;
            clearTimeout(this.closeTimer);
            this.open(item.querySelector('[data-ud-trigger]'));
          }, { signal });
          item.addEventListener('pointerleave', () => {
            this.closeTimer = setTimeout(() => this.close(), 250);
          }, { signal });
        });
      }
      this.addEventListener('click', e => {
        // Nimbus binds every drawer button as a back button. Own our controls
        // during capture so opening a category does not close the entire drawer.
        if (e.target.closest('button')) e.stopPropagation();
        const trigger = e.target.closest('[data-ud-trigger]');
        if (trigger) {
          if (trigger.tagName === 'BUTTON') this.toggle(trigger);
          return;
        }
        if (e.target.closest('[data-ud-close]')) this.close(true);
        if (e.target.closest('[data-ud-next]')) this.move(1);
        if (e.target.closest('[data-ud-prev]')) this.move(-1);
      }, { signal, capture: true });
      this.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.panel) { e.preventDefault(); e.stopPropagation(); this.close(true); }
        if (this.dataset.mode === 'desktop' && this.panel?.contains(e.target) && ['PageDown', 'End'].includes(e.key) && !e.target.closest('input, textarea, select, [contenteditable]')) this.dismissForScroll();
        const trigger = e.target.closest('a[data-ud-trigger]');
        if (e.key === 'ArrowDown' && trigger) {
          e.preventDefault();
          this.open(trigger);
          this.panel?.querySelector('a')?.focus({ preventScroll: true });
        }
      }, { signal });
      this.addEventListener('focusin', () => clearTimeout(this.closeTimer), { signal });
      document.addEventListener('click', e => { if (!this.contains(e.target)) this.close(); }, { signal });
      document.addEventListener('focusin', e => { if (!this.contains(e.target)) this.close(); }, { signal });
      window.addEventListener('resize', () => this.close(), { signal });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; clearTimeout(this.closeTimer); }
    toggle(trigger) {
      if (this.trigger === trigger) { this.close(); return; }
      this.open(trigger);
    }
    open(trigger) {
      clearTimeout(this.closeTimer);
      if (!trigger || this.trigger === trigger) return;
      document.querySelectorAll('ud-smart-menu').forEach(menu => menu.close());
      this.trigger = trigger;
      this.panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!this.panel) return;
      this.panel.hidden = false;
      this.openScrollY = window.scrollY;
      trigger.setAttribute('aria-expanded', 'true');
      this.cards = [...this.panel.querySelectorAll('[data-ud-card]')];
      this.size = this.dataset.mode === 'mobile' ? 2 : 5;
      this.pages = Math.ceil(this.cards.length / this.size);
      this.page = 0; // 2026-09-30: always page 1 (was a daily seed + session memory)
      this.panel.querySelector('.ud-menu-controls').hidden = this.pages <= 1;
      this.panel.querySelector('.ud-menu-visuals').hidden = !this.cards.length;
      this.render();
    }
    close(restore = false) {
      clearTimeout(this.closeTimer);
      if (!this.panel) return;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (restore) this.trigger.focus({ preventScroll: true });
      this.panel = null; this.trigger = null;
    }
    dismissForScroll() {
      if (!this.panel) return;
      this.close(this.panel.contains(document.activeElement));
    }
    move(direction) {
      if (!this.panel || this.pages < 2) return;
      this.page = (this.page + direction + this.pages) % this.pages;
      this.render();
    }
    render() {
      const start = this.page * this.size;
      this.cards.forEach((card, i) => { card.hidden = i < start || i >= start + this.size; });
      this.panel.querySelector('[data-ud-position]').textContent = `${this.page + 1} / ${this.pages}`;
    }
  }
  if (!customElements.get('ud-smart-menu')) customElements.define('ud-smart-menu', UDSmartMenu);

  let observer;
  let lastGrid;
  let lastProductCount = 0;
  const originals = new WeakMap();
  function uniform(grid) {
    grid.classList.remove('ud-editorial-grid');
    grid.querySelectorAll('.ud-editorial-heading, .ud-peek').forEach(node => node.remove());
    const original = originals.get(grid);
    if (original) original.forEach(node => grid.append(node));
    grid.querySelectorAll('img[data-ud-original-sizes]').forEach(img => { img.sizes = img.dataset.udOriginalSizes; });
  }
  function editorial(grid) {
    if (grid.dataset.udEditorial !== 'true') return;
    uniform(grid);
    const children = [...grid.children];
    if (!originals.has(grid)) originals.set(grid, children);
    // Merchant promotion placements remain exactly where they were configured.
    if (children.some(node => !node.hasAttribute('data-ud-product'))) return;
    let remaining = children.slice();
    if (remaining.length < 5) return;
    const frag = document.createDocumentFragment();
    let gap = 0;
    const addGroup = (nodes, columns, title) => {
      if (!nodes.length) return;
      const li = document.createElement('li');
      li.className = 'ud-editorial-heading';
      const heading = document.createElement('h2'); heading.textContent = title; li.append(heading); frag.append(li);
      nodes.forEach(node => { node.dataset.udColumns = String(columns); frag.append(node); });
      const taken = new Set(nodes); remaining = remaining.filter(node => !taken.has(node));
    };
    const available = remaining.filter(node => node.dataset.udAvailable === 'true');
    if (available.length >= 2) addGroup(available.slice(0, 2), 2, grid.dataset.udFeatured);
    if (remaining.length >= 3) addGroup(remaining.slice(0, 3), 3, grid.dataset.udDiscover);
    while (remaining.length) {
      const types = new Map();
      remaining.forEach(node => {
        let type = node.dataset.udType;
        // Catalog imports use both legacy and current names for the same garment.
        if (/cardigan|shrug/i.test(type)) type = 'Cardigans & Shrugs';
        else if (/jacket|blazer/i.test(type)) type = 'Jackets & Blazers';
        else if (/sweatshirt|hoodie/i.test(type)) type = 'Sweatshirts & Hoodies';
        else if (/pant|trouser/i.test(type)) type = 'Pants';
        if (type) { if (!types.has(type)) types.set(type, []); types.get(type).push(node); }
      });
      const cluster = [...types.entries()].find(([, nodes]) => nodes.length >= 8);
      if (cluster) {
        addGroup(cluster[1].slice(0, 8), 4, cluster[0]);
      } else {
        // A named edit/category is itself a coherent comparison group.
        const broad = ['all', 'new-arrivals', 'best-sellers'].includes(grid.dataset.udHandle);
        if (!broad && remaining.length >= 8) addGroup(remaining.slice(0, 8), 4, grid.dataset.udCollection);
        else {
          const count = remaining.length;
          const cols = count === 1 ? 1 : count === 2 || count === 4 ? 2 : 3;
          if (cols === 3 && count % 3) gap = 3 - count % 3;
          addGroup(remaining.slice(), cols, grid.dataset.udMore);
        }
      }
    }
    grid.append(frag); grid.classList.add('ud-editorial-grid');
    // Match the larger editorial images without changing product-card markup or price gates.
    grid.querySelectorAll('[data-ud-product] img').forEach(img => {
      if (!img.dataset.udOriginalSizes) img.dataset.udOriginalSizes = img.sizes;
      img.sizes = '(min-width: 1070px) 50vw, 50vw';
    });
    if (gap) peek(grid, gap);
  }
  // The last "More" row can end short; fill the empty cells with a look at the next page instead of white space.
  const nextPages = new Map();
  async function peek(grid, cells) {
    const next = document.querySelector('#ProductGridContainer a[aria-label="Next page"]')?.href;
    if (!next) return;
    if (!nextPages.has(next)) nextPages.set(next, fetch(next).then(r => r.text()).catch(() => ''));
    const doc = new DOMParser().parseFromString(await nextPages.get(next), 'text/html');
    if (!grid.classList.contains('ud-editorial-grid') || grid.querySelector('.ud-peek')) return;
    const handle = node => node.querySelector('a[href*="/products/"]')?.getAttribute('href').split('/products/')[1]?.split(/[?#]/)[0];
    const seen = new Set([...grid.querySelectorAll('[data-ud-product]')].map(handle));
    const photos = [...doc.querySelectorAll('#product-grid [data-ud-product]')]
      .filter(node => !seen.has(handle(node)))
      .map(node => node.querySelector('img')).filter(Boolean)
      .map(img => (img.getAttribute('srcset') || '').split(',').map(s => s.trim().split(' ')[0]).find(u => /width=[5-7]\d\d\b/.test(u)) || img.getAttribute('src'))
      .filter(Boolean).slice(0, 5);
    if (!photos.length) return;
    const li = document.createElement('li');
    li.className = 'ud-peek';
    li.style.gridColumn = `span ${cells * 4}`;
    li.style.setProperty('--cells', cells);
    const title = grid.dataset.udHandle === 'new-arrivals' ? 'The new arrivals<br>don’t stop here.' : 'There’s more<br>on the next page.';
    li.innerHTML = `<a href="${next}" aria-label="See more on the next page"><div class="ud-peek__frame">
      ${photos.map((u, i) => `<img src="${u}" alt="" loading="lazy"${i ? '' : ' class="is-on"'}>`).join('')}
      <div class="ud-peek__dots" aria-hidden="true">${photos.map((_, i) => `<i${i ? '' : ' class="is-on"'}></i>`).join('')}</div>
      <div class="ud-peek__veil"><p class="ud-peek__eyebrow">Next on the rack</p><p class="ud-peek__title">${title}</p>
      <span class="ud-peek__cta">Keep browsing <span aria-hidden="true">→</span></span></div></div></a>`;
    grid.append(li);
    if (photos.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const pics = li.querySelectorAll('img'), dots = li.querySelectorAll('.ud-peek__dots i');
    let i = 0;
    const timer = setInterval(() => {
      if (!li.isConnected) return clearInterval(timer);
      pics[i].classList.remove('is-on'); dots[i].classList.remove('is-on');
      i = (i + 1) % pics.length;
      pics[i].classList.add('is-on'); dots[i].classList.add('is-on');
    }, 3000);
  }
  function refresh() {
    observer?.disconnect();
    const grid = document.querySelector('#product-grid[data-ud-editorial]');
    if (grid) {
      const allowed = grid.dataset.udEditorial === 'true' && matchMedia('(min-width:1070px)').matches;
      const mode = readSession('ud-collection-layout') || 'editorial';
      if (allowed && mode === 'editorial') editorial(grid); else uniform(grid);
      document.querySelectorAll('[data-ud-layout]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.udLayout === mode)));
    }
    lastGrid = grid;
    lastProductCount = grid?.querySelectorAll('[data-ud-product]').length || 0;
    const target = document.getElementById('ProductGridContainer');
    if (target) {
      observer ||= new MutationObserver(() => {
        const current = document.querySelector('#product-grid[data-ud-editorial]');
        const count = current?.querySelectorAll('[data-ud-product]').length || 0;
        if (current !== lastGrid || count !== lastProductCount) {
          if (current === lastGrid && originals.has(current)) {
            const saved = originals.get(current);
            current.querySelectorAll('[data-ud-product]').forEach(node => { if (!saved.includes(node)) saved.push(node); });
          }
          refresh();
        }
      });
      observer.observe(target, { childList: true, subtree: true });
    }
  }
  document.addEventListener('click', e => {
    const control = e.target.closest('[data-ud-layout]');
    if (control) { saveSession('ud-collection-layout', control.dataset.udLayout); refresh(); }
    if (e.target.closest('.view-mode__button, .view-mode__button-mobile')) { saveSession('ud-collection-layout', 'grid'); refresh(); }
  });
  matchMedia('(min-width:1070px)').addEventListener('change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true }); else refresh();
})();
